import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
    Divider,
    Grid,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
    createFinancePayment,
    fetchFinancePaymentCandidates,
    fetchFinancePayments,
} from '../../../store/slices/financePaymentsSlice';
// We need to import fetchFinanceLedger from the correct slice, which is financeLedgerSlice
import { fetchFinanceLedger as fetchLedger } from '../../../store/slices/financeLedgerSlice';

interface PaymentCreateTabProps {
    selectedCustomerId?: number;
}

const PaymentCreateTab: React.FC<PaymentCreateTabProps> = ({ selectedCustomerId }) => {
    const dispatch = useAppDispatch();
    const paymentsState = useAppSelector((state) => state.financePayments);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    const [paymentForm, setPaymentForm] = useState({
        instrumentNo: '',
        paymentDate: '',
        paymentMode: 'Bank Deposit',
        attachment: null as File | null,
    });
    const [selectedConsignments, setSelectedConsignments] = useState<number[]>([]);
    const [selectedNotes, setSelectedNotes] = useState<number[]>([]);
    const [paymentSuccessMessage, setPaymentSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        if (selectedCustomerId) {
            dispatch(
                fetchFinancePaymentCandidates({
                    customerId: selectedCustomerId,
                })
            );
        }
    }, [dispatch, selectedCustomerId]);

    useEffect(() => {
        if (paymentsState.candidates) {
            // Auto-select all candidates by default or logic can be adjusted
            // For now, let's keep the previous behavior or just empty
            setSelectedConsignments(paymentsState.candidates.consignments.map((c) => c.id));
            setSelectedNotes(paymentsState.candidates.notes.map((n) => n.id));
        }
    }, [paymentsState.candidates]);

    const handlePaymentSelectionToggle = (type: 'consignment' | 'note', id: number) => {
        if (type === 'consignment') {
            setSelectedConsignments((prev) =>
                prev.includes(id) ? prev.filter((existing) => existing !== id) : [...prev, id]
            );
        } else {
            setSelectedNotes((prev) =>
                prev.includes(id) ? prev.filter((existing) => existing !== id) : [...prev, id]
            );
        }
    };

    const computePayableFromSelection = () => {
        if (!paymentsState.candidates) return 0;
        const consignmentsAmount = paymentsState.candidates.consignments
            .filter((c) => selectedConsignments.includes(c.id))
            .reduce(
                (sum, item) =>
                    sum + (item.pricing.cod_amount - item.pricing.service_charges - item.pricing.tax),
                0
            );
        const notesAdjustment = paymentsState.candidates.notes
            .filter((n) => selectedNotes.includes(n.id))
            .reduce(
                (sum, note) => sum + (note.type === 'Debit' ? -(note.amount + note.tax) : note.amount + note.tax),
                0
            );
        return consignmentsAmount + notesAdjustment;
    };

    const handleCreatePayment = async () => {
        if (!selectedCustomerId) {
            setPaymentSuccessMessage('Please select a customer before creating a payment.');
            return;
        }

        if (!selectedConsignments.length) {
            setPaymentSuccessMessage('Select at least one consignment to include in the payment.');
            return;
        }

        try {
            await dispatch(
                createFinancePayment({
                    customerId: selectedCustomerId,
                    consignmentIds: selectedConsignments,
                    noteIds: selectedNotes,
                    instrumentNo: paymentForm.instrumentNo,
                    paymentDate: paymentForm.paymentDate,
                    paymentMode: paymentForm.paymentMode as 'Bank Deposit' | 'Cash in Hand' | 'IBFT',
                    attachment: paymentForm.attachment,
                })
            ).unwrap();

            setPaymentSuccessMessage('Payment created successfully.');
            setPaymentForm({
                instrumentNo: '',
                paymentDate: '',
                paymentMode: 'Bank Deposit',
                attachment: null,
            });

            // Refresh data
            dispatch(fetchFinancePayments({ customerId: selectedCustomerId }));
            // We need to refresh the ledger too, but we don't have the date range here easily.
            // Ideally, the parent component should handle refreshes or we dispatch with empty range for now.
            dispatch(fetchLedger({ customerId: selectedCustomerId }));

            dispatch(
                fetchFinancePaymentCandidates({
                    customerId: selectedCustomerId,
                })
            );

            setSelectedConsignments([]);
            setSelectedNotes([]);
        } catch (error) {
            setPaymentSuccessMessage((error as Error).message);
        }
    };

    return (
        <Stack spacing={2}>
            {paymentSuccessMessage && (
                <Alert
                    severity={paymentSuccessMessage.includes('successfully') ? 'success' : 'error'}
                    onClose={() => setPaymentSuccessMessage(null)}
                >
                    {paymentSuccessMessage}
                </Alert>
            )}
            {!selectedCustomerId && (
                <Alert severity="info">Select a customer to prepare a payment.</Alert>
            )}
            {paymentsState.loadingCandidates && <CircularProgress />}
            {paymentsState.candidates && selectedCustomerId && (
                <Card sx={{ overflow: 'hidden' }}>
                    <CardHeader title="Payment Builder" />
                    <CardContent>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    label="Instrument #"
                                    value={paymentForm.instrumentNo}
                                    onChange={(e) =>
                                        setPaymentForm((prev) => ({ ...prev, instrumentNo: e.target.value }))
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    label="Payment Date"
                                    type="date"
                                    value={paymentForm.paymentDate}
                                    onChange={(e) =>
                                        setPaymentForm((prev) => ({ ...prev, paymentDate: e.target.value }))
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
                                    <Select
                                        labelId="payment-mode-label"
                                        label="Payment Mode"
                                        value={paymentForm.paymentMode}
                                        onChange={(e) =>
                                            setPaymentForm((prev) => ({
                                                ...prev,
                                                paymentMode: e.target.value,
                                            }))
                                        }
                                    >
                                        <MenuItem value="Bank Deposit">Bank Deposit</MenuItem>
                                        <MenuItem value="Cash in Hand">Cash in Hand</MenuItem>
                                        <MenuItem value="IBFT">IBFT</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Button variant="outlined" component="label">
                                    {paymentForm.attachment ? 'Replace Attachment' : 'Upload Attachment'}
                                    <input
                                        hidden
                                        type="file"
                                        accept="application/pdf,image/*"
                                        onChange={(e) =>
                                            setPaymentForm((prev) => ({
                                                ...prev,
                                                attachment: e.target.files?.[0] ?? null,
                                            }))
                                        }
                                    />
                                </Button>
                                {paymentForm.attachment && (
                                    <Typography variant="caption" sx={{ ml: 2 }}>
                                        {paymentForm.attachment.name}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>

                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Eligible Consignments
                        </Typography>
                        {isMobile ? (
                            <Stack spacing={2} sx={{ mb: 2 }}>
                                {paymentsState.candidates.consignments.map((consignment) => (
                                    <Card key={consignment.id} variant="outlined">
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Checkbox
                                                    checked={selectedConsignments.includes(consignment.id)}
                                                    onChange={() =>
                                                        handlePaymentSelectionToggle('consignment', consignment.id)
                                                    }
                                                />
                                                <Box sx={{ flexGrow: 1, ml: 1 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="subtitle2">{consignment.consignment_no}</Typography>
                                                        <Typography variant="subtitle2" fontWeight="bold">Rs. {consignment.pricing.cod_amount.toLocaleString()}</Typography>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {consignment.created_at || '—'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Divider sx={{ mb: 1 }} />
                                            <Grid container spacing={1}>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography variant="caption" color="text.secondary">Status</Typography>
                                                    <Typography variant="body2">{consignment.status.name || '—'}</Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography variant="caption" color="text.secondary">Service Charges</Typography>
                                                    <Typography variant="body2">Rs. {consignment.pricing.service_charges.toLocaleString()}</Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography variant="caption" color="text.secondary">Tax</Typography>
                                                    <Typography variant="body2">Rs. {consignment.pricing.tax.toLocaleString()}</Typography>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        ) : (
                            <Box sx={{ overflowX: 'auto', mb: 2 }}>
                                <Table size="small" sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox" />
                                            <TableCell>Consignment #</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">COD Amount</TableCell>
                                            <TableCell align="right">Service Charges</TableCell>
                                            <TableCell align="right">Tax</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paymentsState.candidates.consignments.map((consignment) => (
                                            <TableRow key={consignment.id}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedConsignments.includes(consignment.id)}
                                                        onChange={() =>
                                                            handlePaymentSelectionToggle('consignment', consignment.id)
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>{consignment.consignment_no}</TableCell>
                                                <TableCell>{consignment.created_at || '—'}</TableCell>
                                                <TableCell>{consignment.status.name || '—'}</TableCell>
                                                <TableCell align="right">
                                                    Rs. {consignment.pricing.cod_amount.toLocaleString()}
                                                </TableCell>
                                                <TableCell align="right">
                                                    Rs. {consignment.pricing.service_charges.toLocaleString()}
                                                </TableCell>
                                                <TableCell align="right">
                                                    Rs. {consignment.pricing.tax.toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Debit / Credit Notes
                        </Typography>
                        {isMobile ? (
                            <Stack spacing={2} sx={{ mb: 2 }}>
                                {paymentsState.candidates.notes.map((note) => (
                                    <Card key={note.id} variant="outlined">
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Checkbox
                                                    checked={selectedNotes.includes(note.id)}
                                                    onChange={() => handlePaymentSelectionToggle('note', note.id)}
                                                />
                                                <Box sx={{ flexGrow: 1, ml: 1 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="subtitle2">Note #{note.unique_id}</Typography>
                                                        <Typography variant="subtitle2" fontWeight="bold">Rs. {note.amount.toLocaleString()}</Typography>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {note.created_at || '—'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Divider sx={{ mb: 1 }} />
                                            <Grid container spacing={1}>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography variant="caption" color="text.secondary">Type</Typography>
                                                    <Typography variant="body2">{note.type}</Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography variant="caption" color="text.secondary">Tax</Typography>
                                                    <Typography variant="body2">Rs. {note.tax.toLocaleString()}</Typography>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        ) : (
                            <Box sx={{ overflowX: 'auto', mb: 2 }}>
                                <Table size="small" sx={{ minWidth: 600 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox" />
                                            <TableCell>Note #</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                            <TableCell align="right">Tax</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paymentsState.candidates.notes.map((note) => (
                                            <TableRow key={note.id}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedNotes.includes(note.id)}
                                                        onChange={() => handlePaymentSelectionToggle('note', note.id)}
                                                    />
                                                </TableCell>
                                                <TableCell>{note.unique_id}</TableCell>
                                                <TableCell>{note.created_at || '—'}</TableCell>
                                                <TableCell>{note.type}</TableCell>
                                                <TableCell align="right">
                                                    Rs. {note.amount.toLocaleString()}
                                                </TableCell>
                                                <TableCell align="right">
                                                    Rs. {note.tax.toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        <Alert severity="info" sx={{ mb: 2 }}>
                            Net Payable Amount:{' '}
                            <strong>Rs. {computePayableFromSelection().toLocaleString()}</strong>
                        </Alert>

                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                                variant="contained"
                                onClick={handleCreatePayment}
                                disabled={paymentsState.creatingPayment}
                            >
                                {paymentsState.creatingPayment ? 'Processing...' : 'Create Payment'}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            )}
        </Stack>
    );
};

export default PaymentCreateTab;
